import { Box, Badge, Button, Typography, Accordion, AccordionSummary, AccordionDetails, Chip } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBasePath, useSession } from 'components/Providers'
import { capitalize } from 'lodash'
import { ROWS_PER_PAGE_OPTIONS } from 'constants'
import { CalendarTodayOutlined, Info } from '@mui/icons-material'
import { deleteAssetDraft } from 'store/asset/need-attention'

export default function RecentlyAdded({ expanded, onChange, showCampus }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { district } = useSession()
  const list = useSelector(state => state.asset.needsAttention.recentlyAdded) || []
  const assetType = useSelector(state => state.lookup.types?.filter(type => type.category === 'assetType') || [])
  const navigate = useNavigate()
  const basePath = useBasePath()
  const [selectedIds, setSelectedIds] = useState([])
  const [deleting, setDeleting] = useState(false)

  const handleAccept = row => {
    navigate(`${basePath}/${row.id}`, { state: { action: 'accept' } })
  }

  const handleDelete = async () => {
    setDeleting(true)
    await dispatch(deleteAssetDraft({ ids: selectedIds }))
    setSelectedIds([])
    setDeleting(false)
  }

  const columns = [
    ...(showCampus ? [{ field: 'campusId', headerName: t('Campus'), flex: 1, renderCell: p => p.row.campus?.name || '' }] : []),
    { field: 'model', headerName: t('Model'), flex: 1 },
    { field: 'brand', headerName: t('Brand'), flex: 1, renderCell: p => capitalize(p.value || '') },
    {
      field: 'typeId',
      headerName: t('Type'),
      flex: 1,
      renderCell: p => {
        const type = assetType.find(t => t.code === p.value)
        return type ? type.name : ''
      }
    },
    {
      field: 'assetNumber',
      headerName: t('Asset Number'),
      flex: 1,
      renderCell: p => p.value ? p.value : <Chip icon={<Info />} color="error" size="small" label={t('Missing')} />,
    },
    {
      field: 'serialNumber',
      headerName: t('Serial Number'),
      flex: 1,
      renderCell: p => p.value ? p.value : <Chip icon={<Info />} color="error" size="small" label={t('Missing')} />,
    },
    {
      field: 'createdAt',
      headerName: t('Date Added'),
      flex: 1,
      renderCell: p => p.value ? <Chip size="small" icon={<CalendarTodayOutlined />} label={new Date(p.value).toLocaleDateString()} /> : '',
    },
    {
      field: 'actions',
      headerName: t('Actions'),
      flex: 1,
      sortable: false,
      renderCell: p => (
        <Button size="small" variant="outlined" onClick={() => handleAccept(p.row)}>
          {t('Review')}
        </Button>
      ),
    },
  ]

  return (
    <>
      <Accordion expanded={expanded} onChange={onChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={2} width="100%">
            <Typography variant="body1" fontWeight={600}>{t('Recently Added Devices')}</Typography>
            <Badge badgeContent={list.length} color="primary" />
            {district?.isServiceProvider && selectedIds.length > 0 && (
              <Button
                size="small"
                variant="contained"
                color="error"
                disabled={deleting}
                onClick={e => { e.stopPropagation(); handleDelete() }}
                sx={{ ml: 'auto', mr: 2 }}
              >
                {t('Delete Records')} ({selectedIds.length})
              </Button>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ overflowX: 'auto' }}>
            <DataGrid
              rows={list}
              columns={columns}
              pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
              autoHeight
              checkboxSelection={district?.isServiceProvider}
              disableRowSelectionOnClick
              rowSelectionModel={selectedIds}
              onRowSelectionModelChange={setSelectedIds}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              sx={{ minWidth: 800 }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  )
}
